"use client";

import Link from "next/link";
import { useEffect, useId, useRef, useState } from "react";
import { track } from "@vercel/analytics";

import { SlotPicker, useDisplayTimeZone } from "@/components/ownly/SlotPicker";
import { adTagsLoaded, bookingEventId, trackBookingConversion } from "@/lib/ad-tags";
import { BOOKED_PATH } from "@/lib/booking";
import {
  bookingIcs,
  bookingNoun,
  displayUkPhone,
  formatLongDay,
  formatTime,
  googleCalendarUrl,
  HOST_TIME_ZONE,
  isUkMobile,
  MANAGE_PATH,
  minutesInWords,
  sameClock,
  ukPhone,
  zoneAbbreviation,
  type Availability,
  type BookingFailure,
  type CallType,
  type MeetingKind,
  type PublicBooking,
} from "@/lib/own-booking";
import { preCallCalendlyNote, type PreCallAnswers } from "@/lib/precall";

/**
 * The calendar step of Ownly's own booking: pick a time, choose Google Meet or a phone call, book
 * it, and see it confirmed with a way to put it in your calendar. Used by /book (after the
 * questions in PreCallForm), by a person's own page (/book/tom), and by /book/applicant for job
 * applicants, which sends no sales answers and fires no ad conversion.
 */

const SECONDARY_BUTTON =
  "inline-flex h-12 items-center justify-center gap-2 rounded-full border border-white/[0.16] bg-white/[0.03] px-5 text-copy font-semibold text-white/90 transition-colors duration-300 hover:border-white/30 hover:bg-white/[0.06] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ownly-accent";

const LINK =
  "font-medium text-ownly-accent-text underline decoration-ownly-accent/35 underline-offset-2 transition-colors hover:text-[#5ca3ff]";

export function AddToCalendar({ booking }: { booking: PublicBooking }) {
  /* A data: address rather than a blob, so there is nothing to create or revoke. */
  const icsHref = `data:text/calendar;charset=utf-8,${encodeURIComponent(bookingIcs(booking))}`;
  return (
    <div className="flex flex-wrap gap-3">
      <a href={googleCalendarUrl(booking)} target="_blank" rel="noopener noreferrer" className={SECONDARY_BUTTON}>
        Add to Google Calendar
      </a>
      <a href={icsHref} download="ownly-booking.ics" className={SECONDARY_BUTTON}>
        Apple or Outlook (.ics)
      </a>
    </div>
  );
}

export function BookedTime({ booking }: { booking: PublicBooking }) {
  const { zone } = useDisplayTimeZone();
  const differs = zone !== HOST_TIME_ZONE && !sameClock(zone, HOST_TIME_ZONE);
  const how = booking.callType === "phone" ? " by phone" : "";
  return (
    <>
      <p className="ownly-display ownly-card-title mt-5 text-balance text-white">
        {formatLongDay(booking.startsAt, zone)} at {formatTime(booking.startsAt, zone)}
      </p>
      <p className="mt-1.5 text-copy text-white/60">
        {minutesInWords(booking.minutes ?? 30)}
        {how}, {zoneAbbreviation(booking.startsAt, zone)}
        {differs ? `. That is ${formatTime(booking.startsAt, HOST_TIME_ZONE)} UK time.` : "."}
      </p>
    </>
  );
}

/**
 * Meet or phone, the number to ring, and the WhatsApp reminder box. Shown above the confirm button
 * only when the meeting type offers a choice or a reminder.
 */
function CallOptions({
  availability,
  callType,
  setCallType,
  phone,
  setPhone,
  whatsapp,
  setWhatsapp,
  phoneError,
}: {
  availability: Availability;
  callType: CallType;
  setCallType: (c: CallType) => void;
  phone: string;
  setPhone: (p: string) => void;
  whatsapp: boolean;
  setWhatsapp: (w: boolean) => void;
  phoneError: boolean;
}) {
  const id = useId();
  const types = availability.meetingType?.callTypes ?? ["meet"];
  const choice = types.includes("meet") && types.includes("phone");
  const mobile = isUkMobile(ukPhone(phone));
  const offerWhatsapp = Boolean(availability.whatsappReminders) && mobile;
  if (!choice && callType === "meet" && !offerWhatsapp) return null;
  return (
    <div className="grid gap-4">
      {choice ? (
        <div role="radiogroup" aria-labelledby={`${id}-how`}>
          <p id={`${id}-how`} className="text-copy font-medium text-white/85">
            How would you like to talk?
          </p>
          <div className="mt-2.5 flex flex-wrap gap-2">
            {(
              [
                { value: "meet", label: "Google Meet video call" },
                { value: "phone", label: "Phone call, we ring you" },
              ] as const
            ).map((o) => (
              <button
                key={o.value}
                type="button"
                role="radio"
                aria-checked={callType === o.value}
                onClick={() => setCallType(o.value)}
                className={`min-h-[44px] rounded-full border px-4 text-copy transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ownly-accent ${
                  callType === o.value
                    ? "border-ownly-accent bg-ownly-accent/20 font-semibold text-white"
                    : "border-white/[0.16] bg-white/[0.02] text-white/80 hover:border-white/[0.32]"
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      ) : null}
      {callType === "phone" ? (
        <label className="grid max-w-[22rem] gap-1.5">
          <span className="text-copy font-medium text-white/85">The UK number we should ring</span>
          <input
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            value={phone}
            maxLength={40}
            onChange={(e) => setPhone(e.target.value)}
            aria-invalid={phoneError}
            aria-describedby={phoneError ? `${id}-phone-error` : undefined}
            placeholder="07700 900123"
            className={`h-12 rounded-xl border bg-white/[0.03] px-4 text-[16px] text-white placeholder:text-white/35 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ownly-accent ${
              phoneError ? "border-[#E05A45]/70" : "border-white/[0.16]"
            }`}
          />
          {phoneError ? (
            <span id={`${id}-phone-error`} className="text-copy text-[#F2AA9D]">
              Please give a UK phone number we can ring, like 07700 900123.
            </span>
          ) : null}
        </label>
      ) : null}
      {offerWhatsapp ? (
        <label className="flex min-h-[44px] max-w-[52ch] items-start gap-3 text-copy leading-[22px] text-white/80">
          <input type="checkbox" checked={whatsapp} onChange={(e) => setWhatsapp(e.target.checked)} className="mt-0.5 h-5 w-5 shrink-0 accent-[#2b7fff]" />
          <span>Send me a WhatsApp reminder on the morning of the call. Just the one message, nothing else.</span>
        </label>
      ) : null}
    </div>
  );
}

export function OwnBookingStep({
  answers,
  campaign,
  fallback,
  contactEmail,
  host = null,
  hostName = null,
  type = null,
  kind = "sales",
}: {
  answers: PreCallAnswers;
  campaign: { source?: string | null; medium?: string | null; campaign?: string | null } | null;
  fallback: React.ReactNode;
  contactEmail: string;
  /** A person's own page (/book/tom). Absent = the shared /book. */
  host?: string | null;
  /** That person's name, for the "not taking bookings yet" message. */
  hostName?: string | null;
  /** Meeting type slug. Absent = the 30-minute review. */
  type?: string | null;
  /** "applicant": a job applicant's chat. No sales answers, no ad conversion, no brief. */
  kind?: MeetingKind;
}) {
  const [booked, setBooked] = useState<{ booking: PublicBooking; token: string } | null>(null);
  const [callType, setCallType] = useState<CallType>("meet");
  const [phone, setPhone] = useState(displayUkPhone(answers.phone));
  const [whatsapp, setWhatsapp] = useState(false);
  const [phoneError, setPhoneError] = useState(false);
  const timeTrackedRef = useRef(false);
  const doneRef = useRef<HTMLDivElement>(null);
  const source = campaign?.source ?? "direct";
  const applicant = kind === "applicant";

  useEffect(() => {
    if (!booked || !doneRef.current) return;
    /* Focus for screen readers, then bring the card's top into view below the fixed header. */
    doneRef.current.focus({ preventScroll: true });
    doneRef.current.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [booked]);

  async function book(slot: string, turnstileToken: string | null, zone: string): Promise<{ ok: true } | { ok: false; reason: BookingFailure }> {
    const uk = ukPhone(phone);
    if (callType === "phone" && !uk) {
      setPhoneError(true);
      return { ok: false, reason: "phone_invalid" };
    }
    setPhoneError(false);
    let json: { ok: true; booking: PublicBooking; token: string } | { ok: false; reason: BookingFailure };
    try {
      const note = applicant ? null : preCallCalendlyNote(answers);
      const res = await fetch("/api/booking/book", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...(host ? { host } : {}),
          ...(type ? { type } : {}),
          start: slot,
          name: answers.name,
          email: answers.email,
          phone: uk ?? (phone.trim() || null),
          callType,
          whatsappOptIn: whatsapp && isUkMobile(uk),
          note,
          answers: note,
          timezone: zone,
          utmSource: campaign?.source ?? null,
          utmMedium: campaign?.medium ?? null,
          utmCampaign: campaign?.campaign ?? null,
          adsConsent: !applicant && adTagsLoaded(),
          turnstileToken,
        }),
      });
      json = (await res.json()) as typeof json;
    } catch {
      return { ok: false, reason: "network" };
    }
    if (!json.ok) {
      if (json.reason === "phone_invalid") setPhoneError(true);
      return json;
    }
    /* Shown first: the booking is made, so nothing after this line may turn it into an error. */
    setBooked({ booking: json.booking, token: json.token });
    try {
      track("booking-completed", { source, system: "own", kind });
      /* The ad tags, if accepted, for prospects only. Same event id as Lead Engine's server copy. */
      if (!applicant && !json.booking.dryRun && json.booking.reference) trackBookingConversion(bookingEventId(json.booking.reference));
    } catch {
      /* Analytics are optional; the booking stands. */
    }
    return { ok: true };
  }

  const noun = booked ? bookingNoun(booked.booking) : applicant ? "chat" : "review";
  const ringing = booked?.booking.callType === "phone";

  return (
    <div className="mt-8">
      <div className="max-w-[42rem] rounded-2xl border border-white/[0.12] bg-gradient-to-b from-white/[0.06] to-ownly-surface/90 px-5 py-6 shadow-[var(--ownly-shadow-card)] sm:px-9 sm:py-8">
        {booked ? (
          <div ref={doneRef} tabIndex={-1} className="ownly-step-in-fwd scroll-mt-32 focus:outline-none" role="status">
            <h3 className="text-body font-semibold text-ownly-accent-text">
              {booked.booking.dryRun ? "Preview only: nothing was booked" : "You're booked in"}
            </h3>
            <BookedTime booking={booked.booking} />
            <p className="mt-5 max-w-[52ch] text-copy leading-[23px] text-white/75">
              {booked.booking.dryRun
                ? "On the live site this is where the calendar invite and our confirmation email would go out. Nothing was sent and nothing is in the diary."
                : ringing
                  ? `We will ring you on your number ${booked.booking.phoneHint ?? "you gave us"}. A calendar invite is on its way to ${answers.email}, with a confirmation from us straight after.`
                  : `A calendar invite with the Google Meet link is on its way to ${answers.email}, with a confirmation from us straight after.`}
            </p>
            <div className="mt-6">
              <AddToCalendar booking={booked.booking} />
            </div>
            <p className="mt-6 max-w-[52ch] text-copy leading-[23px] text-white/65">
              Need to move it?{" "}
              <a href={`${MANAGE_PATH}?t=${encodeURIComponent(booked.token)}`} className={LINK}>
                Change or cancel this booking
              </a>
              . The same link is in your email.
            </p>
            {applicant ? null : (
              <p className="mt-3 text-copy leading-[23px] text-white/65">
                <Link href={BOOKED_PATH} className={LINK}>
                  What happens before the {noun}
                </Link>
              </p>
            )}
          </div>
        ) : (
          <SlotPicker
            host={host}
            hostName={hostName}
            type={type}
            confirmLabel="Book this time"
            busyLabel="Booking it…"
            recipientNote={
              callType === "phone" && ukPhone(phone)
                ? `We will ring ${displayUkPhone(phone)} and send the invite to ${answers.email}.`
                : `We will send the invite to ${answers.email}.`
            }
            needsSecurityCheck
            contactEmail={contactEmail}
            fallback={fallback}
            onConfirm={book}
            extras={(availability) => (
              <CallOptions
                availability={availability}
                callType={callType}
                setCallType={(c) => {
                  setCallType(c);
                  setPhoneError(false);
                }}
                phone={phone}
                setPhone={(p) => {
                  setPhone(p);
                  setPhoneError(false);
                }}
                whatsapp={whatsapp}
                setWhatsapp={setWhatsapp}
                phoneError={phoneError}
              />
            )}
            onTimeChosen={() => {
              if (timeTrackedRef.current) return;
              timeTrackedRef.current = true;
              track("booking-time-selected", { source, system: "own", kind });
            }}
          />
        )}
      </div>
    </div>
  );
}
